import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MessageSquare, Send, BadgeCheck, Camera, KeyRound, LogOut, Building2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { C, FONT, IG_JAWSEL, fmtWaktu, namaKota } from "./ui";
import { Sheet, Empty, chipStyle } from "./Sheet";
import { KTW } from "../kerja/KTW";

const ROLE = { warga: ["Warga", "green"], mayor: ["Walikota", "blue"], admin: ["Admin", "gray"] };
export const TOKEN_KEY = "jawsel_warga_token", PROFILE_KEY = "jawsel_warga";
const inp = { width: "100%", boxSizing: "border-box", padding: "12px 14px", background: C.cream, border: `1.5px solid ${C.line}`, borderRadius: 14, fontSize: 15, fontFamily: FONT, color: C.ink, outline: "none" };
const btnPrimary = { display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8, width: "100%", padding: "13px 18px", border: 0, borderRadius: 99, background: C.blue, color: "#fff", fontSize: 14, fontWeight: 800, cursor: "pointer", fontFamily: FONT };

const sisaCooldown = w => (w?.last_post_at ? Math.max(0, (w.cooldown_total_detik || 10800) - (Date.now() - new Date(w.last_post_at).getTime()) / 1000) : 0);
const fmtDurasi = s => { const m = Math.ceil(s / 60), h = Math.floor(m / 60), mm = m % 60; return h ? (mm ? `${h} jam ${mm} mnt` : `${h} jam`) : `${mm} mnt`; };
export const readProfile = () => { try { return JSON.parse(localStorage.getItem(PROFILE_KEY)); } catch { return null; } };

// Sheet Pesan Warga: feed global (semua bisa baca) + kirim pesan untuk pemegang Kartu Warga (1 pesan / 3 jam).
// Jadi Warga: tombol di footer membuka ringkasan langkah lalu mengarahkan ke Balai Warga (/profil) tempat loket KWJ berada.
export function PesanSheet({ onClose, cityId }) {
  const [feed, setFeed] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [warga, setWarga] = useState(readProfile);
  const [view, setView] = useState("feed"); // feed | verifikasi | kartu
  const [text, setText] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [, setTick] = useState(0);

  useEffect(() => { api.publicPesan().then(d => setFeed(d.pesan || [])).catch(() => setFeed([])); }, []);
  useEffect(() => { const t = setInterval(() => setTick(x => x + 1), 30000); return () => clearInterval(t); }, []);
  useEffect(() => {
    if (!token) return;
    api.wargaMe(token).then(w => { setWarga(w); localStorage.setItem(PROFILE_KEY, JSON.stringify(w)); })
      .catch(e => { if (e.status === 401 || e.status === 403) lepas(e.status === 403 ? "Akun ini diblokir oleh admin" : "Kartu Warga kedaluwarsa — verifikasi ulang"); });
  }, [token]); // eslint-disable-line

  function lepas(m) { localStorage.removeItem(TOKEN_KEY); localStorage.removeItem(PROFILE_KEY); setToken(""); setWarga(null); setView("feed"); setMsg(m || ""); }

  const sisa = sisaCooldown(warga);
  async function kirim(e) {
    e.preventDefault();
    if (!text.trim()) return;
    setBusy(true); setMsg("");
    try {
      const p = await api.wargaPostPesan(token, text.trim());
      setFeed(f => [p, ...(f || [])]); setText("");
      setWarga(w => { const nw = { ...w, last_post_at: p.created_at }; localStorage.setItem(PROFILE_KEY, JSON.stringify(nw)); return nw; });
      toast.success("Pesan terkirim ke seluruh Jawsel");
    } catch (err) {
      setMsg(err.message);
      if (err.status === 401) lepas("Kartu Warga kedaluwarsa — verifikasi ulang");
    }
    setBusy(false);
  }

  if (view === "verifikasi") return <Verifikasi cityId={cityId} onBack={() => setView("feed")} onClose={onClose} />;
  if (view === "kartu" && warga) return (
    <Sheet testid="kartu-sheet" title="Kartu Warga Jawsel" onBack={() => setView("feed")} onClose={onClose} tall={false}>
      <KTW w={warga} />
      <p style={{ margin: "14px 2px 10px", fontSize: 12.5, color: C.sub, lineHeight: 1.5 }}>Kartu ini tersimpan di HP ini selama 30 hari. Dengan kartu ini kamu bisa mengirim 1 pesan tiap 3 jam ke seluruh Jawsel. Masuk lagi kapan saja dengan PIN di Balai Warga.</p>
      <button data-testid="lepas-kartu" onClick={() => { lepas(); toast("Kartu Warga dilepas dari HP ini"); }}
        style={{ ...btnPrimary, background: "#fff", color: C.danger, border: `1.5px solid #fecaca` }}><LogOut size={15} /> Lepas kartu dari HP ini</button>
    </Sheet>
  );

  const footer = warga ? (
    <div>
      <form onSubmit={kirim} data-testid="pesan-publik-form" style={{ display: "flex", gap: 8, alignItems: "center", padding: "2px 2px 2px 14px", background: C.cream, borderRadius: 99, border: `1.5px solid ${text ? C.blue : "transparent"}`, transition: "border-color .15s" }}>
        <input data-testid="pesan-publik-input" value={text} onChange={e => setText(e.target.value)} maxLength={280} disabled={sisa > 0 || busy}
          placeholder={sisa > 0 ? "Tunggu giliran berikutnya..." : "Sapa seluruh Jawsel..."} style={{ flex: 1, minWidth: 0, border: 0, outline: "none", background: "transparent", fontSize: 14, fontFamily: FONT, color: C.ink, padding: "8px 0" }} />
        {text && <span data-testid="pesan-sisa-huruf" style={{ fontSize: 11, color: text.length > 250 ? C.danger : C.sub, fontVariantNumeric: "tabular-nums", flex: "none" }}>{280 - text.length}</span>}
        <button type="submit" data-testid="pesan-publik-send" aria-label="Kirim" disabled={busy || !text.trim() || sisa > 0}
          style={{ width: 38, height: 38, flex: "none", borderRadius: 99, border: 0, background: busy || !text.trim() || sisa > 0 ? "#cfcbbb" : C.blue, color: "#fff", display: "grid", placeItems: "center", cursor: "pointer", transition: "background-color .15s" }}>
          <Send size={16} />
        </button>
      </form>
      {msg && <p data-testid="pesan-publik-msg" style={{ margin: "6px 0 0", fontSize: 12.5, color: C.danger, fontWeight: 600 }}>{msg}</p>}
    </div>
  ) : (
    <div>
      <button data-testid="jadi-warga-btn" onClick={() => setView("verifikasi")} style={btnPrimary}><BadgeCheck size={17} /> Jadi Warga Jawsel untuk kirim pesan</button>
      <p style={{ margin: "8px 2px 0", fontSize: 12, color: C.sub, lineHeight: 1.45, textAlign: "center" }}>Pesan hanya bisa dikirim pemegang Kartu Warga — pengikut Instagram <b>{IG_JAWSEL}</b>.{msg ? ` ${msg}.` : ""}</p>
    </div>
  );

  // Pemisah hari: Hari ini / Kemarin / tanggal
  const hariOf = iso => { const d = new Date(iso), t = new Date(); const diff = Math.floor((new Date(t.getFullYear(), t.getMonth(), t.getDate()) - new Date(d.getFullYear(), d.getMonth(), d.getDate())) / 86400000); return diff <= 0 ? "Hari ini" : diff === 1 ? "Kemarin" : d.toLocaleDateString("id-ID", { day: "numeric", month: "long" }); };
  let hariTerakhir = null;

  // keterangan jatah kirim di sub-judul (bukan baris identitas di footer): "1 pesan / 3 jam" atau sisa cooldown
  const jatah = warga ? (sisa > 0 ? `Berikutnya dalam ${fmtDurasi(sisa)}` : "1 pesan / 3 jam") : null;
  return (
    <Sheet testid="pesan-sheet" title="Pesan Warga" sub={jatah ? `Seluruh Jawsel · ${jatah}` : `Papan pesan seluruh Jawsel${feed ? ` · ${feed.length} pesan` : ""}`} icon={MessageSquare} onClose={onClose} footer={footer}
      aksi={warga ? <button data-testid="kartu-warga-btn" aria-label="Kartu Warga" title={`${warga.nama} @${warga.ig_username}`} onClick={() => setView("kartu")} style={{ width: 36, height: 36, borderRadius: 99, border: 0, background: "#d6f2e2", color: "#189a5b", display: "grid", placeItems: "center", cursor: "pointer", flex: "none" }}><BadgeCheck size={17} /></button> : null}>
      {feed === null && <p style={{ color: C.sub, fontSize: 13, padding: "8px 2px" }}>Memuat pesan...</p>}
      {feed && feed.length === 0 && <Empty icon={MessageSquare} testid="pesan-publik-empty" text="Belum ada pesan. Jadilah warga pertama yang menyapa!" />}
      <div data-testid="pesan-publik-list" style={{ display: "flex", flexDirection: "column", gap: 8, paddingTop: 2 }}>
        {(feed || []).map((p, i) => {
          const [label, tone] = ROLE[p.author?.role] || ROLE.admin;
          const mine = warga && p.author?.id === warga.id;
          const hari = hariOf(p.created_at), pisah = hari !== hariTerakhir; hariTerakhir = hari;
          return (
            <React.Fragment key={p.id}>
              {pisah && (
                <div data-testid="pesan-hari" style={{ display: "flex", alignItems: "center", gap: 10, margin: i === 0 ? "2px 0 2px" : "8px 0 2px" }}>
                  <span style={{ flex: 1, height: 1, background: C.line }} />
                  <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: 1.1, textTransform: "uppercase", color: C.sub }}>{hari}</span>
                  <span style={{ flex: 1, height: 1, background: C.line }} />
                </div>
              )}
              {/* kartu kaca: teks langsung (tanpa avatar & balon) — nama · peran · kota di atas, waktu di kanan */}
              <article data-testid={`pesan-publik-${p.id}`} style={{ padding: "11px 14px 12px", borderRadius: 18, background: mine ? "rgba(0,0,205,.045)" : "rgba(244,241,230,.72)", border: `1px solid ${mine ? "rgba(0,0,205,.18)" : "rgba(20,20,20,.06)"}`, backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)", boxShadow: "inset 0 1px 0 rgba(255,255,255,.8)" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, lineHeight: 1.2, minWidth: 0 }}>
                  <b style={{ fontSize: 13, letterSpacing: -0.1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{p.author?.nama}{mine ? <span style={{ color: C.blue, fontWeight: 700 }}> · kamu</span> : null}</b>
                  <span style={{ ...chipStyle(tone), padding: "2px 7px", fontSize: 9.5, flex: "none", ...(tone === "gray" ? { background: "#fff", border: "1px solid rgba(20,20,20,.08)", color: C.ink } : {}) }}>{label}</span>
                  <span style={{ fontSize: 11, color: C.sub, flex: "none", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{p.author?.city_id ? namaKota(p.author.city_id) : "Jawsel"}</span>
                  <span style={{ fontSize: 11, color: C.sub, marginLeft: "auto", flex: "none", fontVariantNumeric: "tabular-nums" }}>{fmtWaktu(p.created_at)}</span>
                </div>
                <p style={{ margin: "6px 0 0", fontSize: 14, lineHeight: 1.5, color: C.ink, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{p.text}</p>
              </article>
            </React.Fragment>
          );
        })}
      </div>
    </Sheet>
  );
}

// Jadi Warga: pengurusan KWJ dipusatkan di Balai Warga (/profil) — loket 4 langkah (daftar → kode via DM → PIN → cetak)
export function Verifikasi({ onBack, onClose, cityId }) {
  const nav = useNavigate();
  const steps = [
    [Camera, <>Ikuti <b>{IG_JAWSEL}</b> di Instagram</>],
    [MessageSquare, <>Ambil nomor antrean di Balai Warga, lalu <b>kirim kode</b> yang muncul lewat DM ke {IG_JAWSEL}</>],
    [KeyRound, <>Buat PIN 6 angka — KWJ terbit, kamu jadi <b>Warga Asli{cityId ? ` Kota ${namaKota(cityId)}` : ""}</b></>],
  ];
  return (
    <Sheet testid="verifikasi-sheet" title="Jadi Warga Jawsel" sub="Urus Kartu Warga Jawsel di Balai Warga, ±1 menit" onBack={onBack} onClose={onClose} tall={false}>
      <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 14 }}>
        {steps.map(([Icon, node], i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 12px", background: C.cream, borderRadius: 16 }}>
            <span style={{ width: 34, height: 34, borderRadius: 12, background: "#fff", display: "grid", placeItems: "center", flex: "none", position: "relative" }}>
              <Icon size={15} color={C.blue} />
              <span style={{ position: "absolute", top: -6, left: -6, width: 18, height: 18, borderRadius: 99, background: C.blue, color: "#fff", fontSize: 10.5, fontWeight: 800, display: "grid", placeItems: "center" }}>{i + 1}</span>
            </span>
            <span style={{ fontSize: 13, lineHeight: 1.45 }}>{node}</span>
          </div>
        ))}
      </div>
      <button data-testid="ke-balai-warga" onClick={() => nav(`/profil?kota=${cityId || ""}`)} style={btnPrimary}><Building2 size={16} /> Buka Balai Warga</button>
      <button data-testid="ke-balai-masuk" onClick={() => nav(`/profil?kota=${cityId || ""}`)} style={{ ...btnPrimary, background: "transparent", color: C.ink, marginTop: 6, fontWeight: 700 }}>Sudah punya KWJ? Masuk dengan PIN</button>
    </Sheet>
  );
}
